import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Modal, ModalBody, ModalHeader } from '@cfa/react-components';

import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';

import { itemByLanguage } from '@/utils/language';

import { TemplateCard } from './TemplateCard';

import { Plan } from '@/_generated/api-clients/xp';

const CATEGORY_ORDER = [
  'compliance',
  'back_of_house',
  'front_of_house',
  'general',
  'hospitality',
  'leadership',
  'onboarding',
];

const getCategoryIndex = (category: string | undefined) => {
  const index = CATEGORY_ORDER.indexOf(category ?? '');
  return index === -1 ? CATEGORY_ORDER.length : index;
};

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCopy: (id: string) => void;
  onPreviewTemplateClick: (template: Plan) => void;
  templates: Plan[];
  onAdd: (template: Plan) => void;
  subscribingTemplateId?: string;
  isPending?: boolean;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen,
  onClose,
  onCopy,
  onPreviewTemplateClick,
  templates,
  onAdd,
  subscribingTemplateId,
  isPending = false,
}) => {
  const { t } = useTranslation();

  const sortedTemplates = [...templates].sort(
    (a, b) =>
      getCategoryIndex(a.category) - getCategoryIndex(b.category) ||
      itemByLanguage(a.name, '').localeCompare(itemByLanguage(b.name, '')),
  );

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [refReady, setRefReady] = useState(false);

  // Use callback ref to track when ref becomes available
  const setModalBodyRef = (node: HTMLDivElement | null) => {
    modalBodyRef.current = node;
    setRefReady(!!node);
  };

  useScrollMemory({
    container: modalBodyRef,
    key: 'template-modal',
    enabled: isOpen && refReady,
  });

  return (
    <Modal
      className="[&_.cfa-modal-header]:pb-6!"
      onClose={onClose}
      scrollMode="modal-body"
      show={isOpen}
      size="lg"
    >
      <ModalHeader>{t('TrainingPlans.templates.templateModal')}</ModalHeader>
      <ModalBody ref={setModalBodyRef} className="flex flex-col gap-4">
        {sortedTemplates.map(template => (
          <TemplateCard
            key={template.id}
            onCopy={() => {
              if (template.id) {
                onCopy(template.id);
              }
            }}
            onPreview={() => {
              onPreviewTemplateClick(template);
            }}
            onAdd={() => onAdd(template)}
            plan={template}
            isLoading={isPending && subscribingTemplateId === template.id}
          />
        ))}
      </ModalBody>
    </Modal>
  );
};

export default TemplateModal;
